import React, { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  ACCOUNT_DETAILS_BIRTH_YEAR_LABEL, ACCOUNT_DETAILS_CATEGORIES_LABEL, ACCOUNT_DETAILS_CATEGORY_OPTIONS,
  ACCOUNT_DETAILS_LOCATION_LABEL, ACCOUNT_DETAILS_POSTAL_CODE_LABEL, ACCOUNT_DETAILS_POSTAL_CODE_PLACEHOLDER, ACCOUNT_DETAILS_REGION_EMPTY,
  ACCOUNT_DETAILS_REGION_LABEL, ACCOUNT_DETAILS_TITLE, accountDetailsRequest, birthYearRange, displayBundesland,
  postalCodeError, postalCodeOfLocation, REGION_GROUPS, regionFromLocation, UpdateUserDetailsRequest, User,
} from '@radevents/shared';
import { useAuth } from '../contexts/AuthContext';
import { ORGANIZER_ACCESS_CSS } from './organizer/OrganizerAccessDialog';

/**
 * Radarten als Kästchen zum Ankreuzen, keine Pillen: die Pillenform zeigt anderswo einen Zustand.
 * Lage des Hakens und Farbe sagen zusammen, was gewählt ist.
 */
const ACCOUNT_DETAILS_CSS = `
.ad-options { display:flex; flex-wrap:wrap; gap:8px; margin-top:6px; }
.ad-option { position:relative; flex:1 1 auto; display:flex; align-items:center; gap:8px; padding:10px 11px; border:1px solid #E5E1E1;
  border-radius:8px; background:#fff; cursor:pointer; font-family:'Hanken Grotesk',sans-serif; font-size:14px;
  font-weight:600; color:#2C2C2C; }
.ad-option:hover { border-color:#922C57; }
.ad-option input { position:absolute; opacity:0; pointer-events:none; }
.ad-option:has(input:focus-visible) { outline:2px solid #922C57; outline-offset:2px; }
.ad-box { width:18px; height:18px; border-radius:5px; border:1.5px solid #C9C3C3; flex-shrink:0;
  display:flex; align-items:center; justify-content:center; color:#fff; }
.ad-option--on { border-color:#922C57; background:#FFF6F9; }
.ad-option--on .ad-box { background:#922C57; border-color:#922C57; }
.ad-label { display:block; font-family:'Space Mono',monospace; font-size:10px; letter-spacing:.1em; text-transform:uppercase; color:#9A9494; }
.ad-input { width:100%; max-width:160px; box-sizing:border-box; margin-top:6px; border:1px solid #E5E1E1; border-radius:8px;
  padding:11px 12px; font-family:'Hanken Grotesk',sans-serif; font-size:15px; color:#2C2C2C; background:#fff; }
.ad-input:focus { outline:none; border-color:#922C57; }
.ad-input--select { max-width:none; }
.ad-row { display:flex; gap:12px; }
.ad-row > label { flex:1; min-width:0; }
.ad-row .ad-input { max-width:none; }
`;

interface FieldsProps {
  birthYear: string;
  categories: string[];
  onBirthYearChange: (v: string) => void;
  onCategoriesChange: (v: string[]) => void;
  idPrefix: string;
  /**
   * Der dritte Punkt: nach dem Anlegen die Postleitzahl (die Region folgt daraus), im Profil die
   * Region selbst, weil der Standort dort ein eigenes Feld hat. Siehe `shared/src/accountDetails.ts`.
   */
  place: { kind: 'postalCode' | 'region'; value: string; onChange: (v: string) => void };
  /** Nur im Profil: der Standort (PLZ oder Ort) neben der Region, weil das eine aus dem anderen folgt. */
  location?: { value: string; onChange: (v: string) => void };
}

/** Die Felder ohne Hülle: im Fenster nach dem Anlegen und im Profil dieselben. */
export const AccountDetailsFields: React.FC<FieldsProps> = ({
  birthYear, categories, onBirthYearChange, onCategoriesChange, idPrefix, place, location,
}) => {
  const { max } = birthYearRange();
  const toggle = (value: string) => onCategoriesChange(
    categories.includes(value) ? categories.filter(c => c !== value) : [...categories, value],
  );
  return (
    <>
      <style>{ACCOUNT_DETAILS_CSS}</style>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend className="ad-label">{ACCOUNT_DETAILS_CATEGORIES_LABEL}</legend>
        <div className="ad-options">
          {ACCOUNT_DETAILS_CATEGORY_OPTIONS.map(o => {
            const on = categories.includes(o.value);
            return (
              <label key={o.value} className={`ad-option${on ? ' ad-option--on' : ''}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(o.value)} />
                <span className="ad-box" aria-hidden>{on && <Check size={13} strokeWidth={3} />}</span>
                {o.label}
              </label>
            );
          })}
        </div>
      </fieldset>
      {place.kind === 'postalCode' ? (
        // Zwei kurze Zahlen: nebeneinander, statt untereinander zwei halbleere Zeilen.
        <div className="ad-row">
          <label htmlFor={`${idPrefix}-birth-year`}>
            <span className="ad-label">{ACCOUNT_DETAILS_BIRTH_YEAR_LABEL}</span>
            <input
              id={`${idPrefix}-birth-year`} className="ad-input" inputMode="numeric" maxLength={4}
              placeholder={`z. B. ${max - 25}`} value={birthYear}
              onChange={e => onBirthYearChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
            />
          </label>
          <label htmlFor={`${idPrefix}-postal-code`}>
            <span className="ad-label">{ACCOUNT_DETAILS_POSTAL_CODE_LABEL}</span>
            <input
              id={`${idPrefix}-postal-code`} className="ad-input" inputMode="numeric" maxLength={5} autoComplete="postal-code"
              placeholder={ACCOUNT_DETAILS_POSTAL_CODE_PLACEHOLDER} value={place.value}
              onChange={e => place.onChange(e.target.value.replace(/\D/g, '').slice(0, 5))}
            />
          </label>
        </div>
      ) : (
        <>
          {/* Mit Standort (Profil) wie oben: zwei kurze Felder nebeneinander, die Region darunter. */}
          <div className={location ? 'ad-row' : undefined}>
            <label htmlFor={`${idPrefix}-birth-year`}>
              <span className="ad-label">{ACCOUNT_DETAILS_BIRTH_YEAR_LABEL}</span>
              <input
                id={`${idPrefix}-birth-year`} className="ad-input" inputMode="numeric" maxLength={4}
                placeholder={`z. B. ${max - 25}`} value={birthYear}
                onChange={e => onBirthYearChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            </label>
            {location && (
              <label htmlFor={`${idPrefix}-location`}>
                <span className="ad-label">{ACCOUNT_DETAILS_LOCATION_LABEL}</span>
                <input
                  id={`${idPrefix}-location`} className="ad-input" autoComplete="postal-code"
                  placeholder={ACCOUNT_DETAILS_POSTAL_CODE_PLACEHOLDER} value={location.value}
                  onChange={e => location.onChange(e.target.value)}
                />
              </label>
            )}
          </div>
          <label htmlFor={`${idPrefix}-region`}>
            <span className="ad-label">{ACCOUNT_DETAILS_REGION_LABEL}</span>
            <select id={`${idPrefix}-region`} className="ad-input ad-input--select" value={place.value} onChange={e => place.onChange(e.target.value)}>
              <option value="">{ACCOUNT_DETAILS_REGION_EMPTY}</option>
              {REGION_GROUPS.map(g => (
                <optgroup key={g.label} label={g.label}>
                  {g.regions.map(r => <option key={r} value={r}>{displayBundesland(r)}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
        </>
      )}
    </>
  );
};

/** Formularzustand aus dem Konto. */
export const useAccountDetailsForm = (user: User | null) => {
  const [birthYear, setBirthYear] = useState(user?.birthYear ? String(user.birthYear) : '');
  const [categories, setCategories] = useState<string[]>(user?.preferredCategories ?? []);
  // Die Region kann sich auch von außen ändern (Mitmachen bei der Winterchallenge setzt sie).
  const [region, setRegion] = useState(user?.bundesland ?? '');
  useEffect(() => { setRegion(user?.bundesland ?? ''); }, [user?.bundesland]);
  const [postalCode, setPostalCode] = useState(postalCodeOfLocation(user?.location));
  const [location, setLocationValue] = useState(user?.location ?? '');
  // Die Region folgt dem Standort, solange sie leer ist oder zuletzt aus ihm folgte. Gemerkt wird
  // die zuletzt abgeleitete, nicht die aus dem Feld: beim Tippen ist „3065“ noch keine PLZ, und
  // die Region gälte sonst ab dort als selbst gewählt. Eine selbst gewählte (etwa fürs Ausland)
  // überschreibt eine neue Postleitzahl nicht.
  const [derivedRegion, setDerivedRegion] = useState(regionFromLocation(user?.location));
  const setLocation = (value: string) => {
    const derived = regionFromLocation(value);
    if (derived && (!region || region === derivedRegion)) {
      setRegion(derived);
      setDerivedRegion(derived);
    }
    setLocationValue(value);
  };
  return {
    birthYear, setBirthYear, categories, setCategories, region, setRegion, postalCode, setPostalCode,
    location, setLocation,
  };
};

/** Speichert, was sich geändert hat: Radarten und Geburtsjahr gemeinsam, die Region über ihren eigenen Weg. */
export const useSaveAccountDetails = () => {
  const { user, updateDetails, updateRegion } = useAuth();
  return async (request: UpdateUserDetailsRequest, region: string) => {
    await updateDetails(request);
    if ((user?.bundesland ?? '') !== region) await updateRegion(region || null);
  };
};

/**
 * Einmal direkt nach dem Anlegen eines Kontos über einen Einmal-Code, auf jeder Seite
 * (`detailsPromptOpen` im AuthContext). Überspringen ist gleichwertig: gefragt wird nicht wieder,
 * das Profil hat dieselben Felder.
 */
export const AccountDetailsDialog: React.FC = () => {
  const { user, detailsPromptOpen, closeDetailsPrompt, updateDetails, updateLocation } = useAuth();
  const form = useAccountDetailsForm(user);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!detailsPromptOpen || !user) return null;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const { request, error: invalid } = accountDetailsRequest(form.birthYear, form.categories);
    if (!request) { setError(invalid ?? null); return; }
    const plzError = postalCodeError(form.postalCode);
    if (plzError) { setError(plzError); return; }
    const postalCode = form.postalCode.trim();
    if (request.birthYear == null && request.preferredCategories.length === 0 && !postalCode) { closeDetailsPrompt(); return; }
    setBusy(true);
    setError(null);
    try {
      await updateDetails(request);
      // Als Standort gespeichert; die Region leitet `updateLocation` daraus ab.
      if (postalCode && postalCode !== postalCodeOfLocation(user.location)) await updateLocation(postalCode);
      toast.success('Angaben gespeichert');
      closeDetailsPrompt();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Die Angaben ließen sich nicht speichern.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="oa-backdrop" role="dialog" aria-modal="true" aria-label={ACCOUNT_DETAILS_TITLE}>
      <style>{ORGANIZER_ACCESS_CSS}</style>
      <div className="oa-dialog">
        <div className="oa-top">
          <span className="oa-step">Dein Konto ist da</span>
          <button type="button" className="oa-icon-btn" onClick={closeDetailsPrompt} aria-label="Überspringen"><X size={16} /></button>
        </div>
        <h2 className="oa-title">{ACCOUNT_DETAILS_TITLE}</h2>
        <form className="oa-body" onSubmit={save}>
          <AccountDetailsFields
            idPrefix="ad-dialog"
            birthYear={form.birthYear} categories={form.categories}
            onBirthYearChange={form.setBirthYear} onCategoriesChange={form.setCategories}
            place={{ kind: 'postalCode', value: form.postalCode, onChange: form.setPostalCode }}
          />
          {error && <p className="oa-hint" style={{ color: '#8C3B0B' }}>{error}</p>}
          <div className="oa-actions">
            <button type="submit" className="oa-btn oa-btn--primary" disabled={busy}>
              {busy ? 'Wird gespeichert…' : 'Speichern'}
            </button>
            <button type="button" className="oa-btn" onClick={closeDetailsPrompt} disabled={busy}>Überspringen</button>
          </div>
        </form>
      </div>
    </div>
  );
};
